import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useLanguage } from '@/contexts/language-context';
import type { PaymentMethodDto } from '@/lib/api/payment-methods';

import { ConfigModal } from './config-modal';
import type { MethodType } from './types';

interface Props {
  methodType: MethodType;
  // Present when editing an existing entry — otherwise this is a create form.
  initial?: PaymentMethodDto;
  onSave: (data: { label: string; config: Record<string, unknown>; is_active: boolean }) => void;
  onClose: () => void;
  isSaving: boolean;
}

export function PaymentMethodFormModal({ methodType, initial, onSave, onClose, isSaving }: Props) {
  const { t } = useLanguage();
  const config = (initial?.config ?? {}) as Record<string, unknown>;

  const title =
    methodType === 'zelle'
      ? t.zelleTitle
      : methodType === 'venmo'
        ? t.venmoTitle
        : methodType === 'cash'
          ? t.cashTitle
          : t.checkTitle;

  const [label, setLabel] = useState(initial?.label ?? '');
  const [phone, setPhone] = useState(typeof config.phone === 'string' ? config.phone : '');
  const [handle, setHandle] = useState(typeof config.handle === 'string' ? config.handle : '');
  const [isActive, setIsActive] = useState(initial?.is_active ?? true);

  const requiresDetail = methodType === 'zelle' || methodType === 'venmo';
  const detailValue = methodType === 'zelle' ? phone : handle;
  const canSave = !requiresDetail || detailValue.trim() !== '';

  function handleSave() {
    if (!canSave) return;
    const nextConfig: Record<string, unknown> = {};
    if (methodType === 'zelle' && phone.trim()) nextConfig.phone = phone.trim();
    if (methodType === 'venmo' && handle.trim()) nextConfig.handle = handle.trim();
    onSave({ label: label.trim(), config: nextConfig, is_active: isActive });
  }

  return (
    <ConfigModal title={title} onClose={onClose}>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="payment-method-label">{t.paymentMethodLabelField}</Label>
        <Input
          id="payment-method-label"
          placeholder={t.paymentMethodLabelPlaceholder}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          data-testid="payment-method-label-input"
        />
      </div>

      {methodType === 'zelle' && (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="payment-method-phone">{t.zellePhone}</Label>
          <Input
            id="payment-method-phone"
            placeholder={t.zellePhonePlaceholder}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            data-testid="payment-method-phone-input"
          />
        </div>
      )}

      {methodType === 'venmo' && (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="payment-method-handle">{t.venmoUsername}</Label>
          <Input
            id="payment-method-handle"
            placeholder={t.venmoUsernamePlaceholder}
            value={handle}
            onChange={(e) => setHandle(e.target.value)}
            data-testid="payment-method-handle-input"
          />
        </div>
      )}

      {initial && (
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            data-testid="payment-method-active-checkbox"
          />
          {t.paymentMethodActive}
        </label>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" size="sm" onClick={onClose}>
          {t.cancel}
        </Button>
        <Button
          size="sm"
          onClick={handleSave}
          disabled={!canSave || isSaving}
          data-testid="payment-method-save"
        >
          {t.saveConfig}
        </Button>
      </div>
    </ConfigModal>
  );
}
